import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { RowSelector } from 'src/components/Selectors/SectionRowSelector';
import { RowInfo, SectionInfo } from 'src/WebApiController';

import * as styles from './FilterPillList.css';

const PENCIL_CHAR = '\u270E';

export const RowIdFilterDropdown = ({
  rowName,
  row,
  section,
  focusedSectionIds,
  onChange,
  onTriggerFocus,
  disabled,
  contentFooter,
  rowRange,
  error,
  useAnimatedBorder,
}: {
  rowName?: string | null;
  row?: RowInfo;
  section?: SectionInfo;
  focusedSectionIds: number[];
  onChange: (row: RowInfo) => void;
  onTriggerFocus?: (sectionId: number) => void;
  disabled: boolean;
  contentFooter?: React.ReactNode;
  rowRange?: [number, number];
  error?: boolean;
  useAnimatedBorder?: boolean;
}) => {
  const onRowChange = useCallback(
    (rowId: string) => {
      const rowIdNum = parseInt(rowId);
      const row = section?.rows?.find((r) => r.id === rowIdNum);
      if (row) {
        onChange(row);
      }
    },
    [onChange, section?.rows]
  );

  const idToName = useMemo(() => {
    return (section?.rows ?? []).reduce(
      (res, row) => {
        res[row.id] = row.name ?? '';
        return res;
      },
      {} as Record<number, string>
    );
  }, [section?.rows]);

  const displayText = useMemo(() => {
    if (rowRange) {
      const [lower, upper] = rowRange;
      if (idToName[lower] && idToName[upper]) {
        return ` ${idToName[lower]} - ${idToName[upper]}`;
      }
    }
    return `< ${row?.name}`;
  }, [idToName, row?.name, rowRange]);

  if (!section?.rows?.filter((r) => !r.isSpeculative)?.length) {
    return null;
  }

  return (
    <RowSelector
      className={styles.mutablePillItemFilterText}
      triggerContentClassName={clsx({
        [styles.animatedText]: useAnimatedBorder,
        [styles.errorText]: error,
      })}
      rootProps={{
        onOpenChange: (open: boolean) => {
          if (open && section) {
            const alreadyFocused = focusedSectionIds.includes(section.id);
            if (!alreadyFocused) {
              onTriggerFocus?.(section.id);
            }
          }
        },
      }}
      hideFoldButton
      searchable={false}
      placeholderText={rowRange ? displayText : `< ${rowName || PENCIL_CHAR}`}
      value={row?.id.toString() ?? ''}
      displayText={displayText}
      selectedSection={section}
      disabled={disabled}
      onChange={onRowChange}
      onMouseDown={(e) => {
        const alreadyFocused = focusedSectionIds.includes(section.id);
        if (!alreadyFocused) {
          onTriggerFocus?.(section!.id);
        }
      }}
      onClick={(e) => {
        const alreadyFocused = focusedSectionIds.includes(section.id);
        if (!alreadyFocused) {
          onTriggerFocus?.(section!.id);
        }
        // Prevent the click from bubbling to the pill container which toggles focus
        // We manage focus explicitly above to avoid double toggling
        e.stopPropagation();
      }}
      contentFooter={contentFooter}
    />
  );
};
